import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { villas, OWNER, FAMILY_PACKAGES } from "@/data/villas";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Check,
  ArrowRight,
  Phone,
  Mail,
  Baby,
  Coffee,
  Car,
  Flower2,
  Sparkles,
  Utensils,
  Plane,
  UserCheck,
  Home as HomeIcon,
  Wine,
  Waves,
  Luggage,
  FileDown,
  Loader2,
} from "lucide-react";

const HERO = villas.hasna.gallery[0];
const AMBIANCE = villas.hasna.gallery[2];
const HOUSE_IMG = villas.hasna.gallery[1];

const BONUS_ICONS = [HomeIcon, UserCheck, Sparkles, Wine, Luggage, Waves];

const EXTRA_ICON = {
  breakfast: Coffee,
  shuttle: Plane,
  nannyEvening1: Baby,
  halfBoard: Utensils,
  dailyTransport: Car,
  nannyDayEvening: Baby,
  massageParents: Flower2,
  chauffeur: Car,
  nanny8h2evenings: Baby,
};

function BrochureForm({ defaultFormula = "any", onSuccess }) {
  const { t, lang } = useLang();
  const b = t.brochure;
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [formula, setFormula] = useState(defaultFormula);
  const [stayDates, setStayDates] = useState("");
  const [guests, setGuests] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/family-leads", {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        formula,
        stay_dates: stayDates,
        guests,
        message,
        language: lang,
      });
      toast.success(b.success);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || b.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="brochure-form" className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="dh-overline">{b.firstName}</Label>
          <Input required data-testid="brochure-firstname" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="h-11 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
        </div>
        <div className="space-y-2">
          <Label className="dh-overline">{b.lastName}</Label>
          <Input required data-testid="brochure-lastname" value={lastName} onChange={(e) => setLastName(e.target.value)} className="h-11 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="dh-overline">{b.email}</Label>
          <Input required type="email" data-testid="brochure-email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
        </div>
        <div className="space-y-2">
          <Label className="dh-overline">{b.phone}</Label>
          <Input data-testid="brochure-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="dh-overline">{b.formulaLabel}</Label>
        <Select value={formula} onValueChange={setFormula}>
          <SelectTrigger data-testid="brochure-formula" className="h-11 rounded-none border-[color:hsl(var(--dh-ocre)/0.4)] bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{b.formulaAny}</SelectItem>
            <SelectItem value="essentielle">{b.formulaEssentielle}</SelectItem>
            <SelectItem value="confort">{b.formulaConfort}</SelectItem>
            <SelectItem value="premium">{b.formulaPremium}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="dh-overline">{b.stayDates}</Label>
          <Input data-testid="brochure-dates" placeholder={b.stayDatesPh} value={stayDates} onChange={(e) => setStayDates(e.target.value)} className="h-11 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
        </div>
        <div className="space-y-2">
          <Label className="dh-overline">{b.guests}</Label>
          <Input data-testid="brochure-guests" placeholder={b.guestsPh} value={guests} onChange={(e) => setGuests(e.target.value)} className="h-11 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="dh-overline">{b.message}</Label>
        <Textarea rows={3} data-testid="brochure-message" value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
      </div>
      <p className="text-xs text-[color:hsl(var(--dh-muted))]">{b.privacy}</p>
      <button type="submit" disabled={submitting} data-testid="brochure-submit" className="dh-btn-primary w-full py-4 disabled:opacity-60">
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {b.submitting}</> : b.submit}
      </button>
    </form>
  );
}

function BrochureCta({ defaultFormula, variant = "primary" }) {
  const { t } = useLang();
  const b = t.brochure;
  const [open, setOpen] = useState(false);
  const cls =
    variant === "primary"
      ? "inline-flex items-center gap-2 bg-[color:hsl(var(--dh-terracotta))] text-white px-6 py-3 text-xs uppercase tracking-widest hover:bg-[color:hsl(var(--dh-terracotta-dark))] transition-colors"
      : "inline-flex items-center gap-2 border border-[color:hsl(var(--dh-ocre))] text-[color:hsl(var(--dh-sand))] px-6 py-3 text-xs uppercase tracking-widest hover:bg-[color:hsl(var(--dh-ocre)/0.15)] transition-colors";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="brochure-cta" className={cls}>
          <FileDown className="h-4 w-4" />
          {b.cta}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl bg-[color:hsl(var(--dh-sand))] p-8 rounded-none">
        <div>
          <div className="dh-overline">{b.modalOverline}</div>
          <h3 className="font-serif text-3xl mt-2">{b.modalTitle}</h3>
          <p className="mt-3 text-sm text-[color:hsl(var(--dh-muted))]">{b.modalSubtitle}</p>
        </div>
        <div className="mt-6">
          <BrochureForm defaultFormula={defaultFormula} onSuccess={() => setOpen(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SejourFamille() {
  const { t } = useLang();
  const f = t.family;

  return (
    <>
      <SEO pageKey="family" />
      {/* HERO */}
      <section data-testid="family-hero" className="relative -mt-20 h-[90svh] overflow-hidden">
        <img src={HERO} alt={f.pageTitle} className="absolute inset-0 h-full w-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/70" />
        <div className="relative z-10 h-full dh-container flex flex-col justify-end pb-24 text-white">
          <div className="fade-up max-w-3xl">
            <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">{f.overline}</div>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] mt-4">
              {f.title}
              <span className="block italic text-[color:hsl(var(--dh-ocre))]">{f.titleItalic}</span>
            </h1>
            <p className="mt-6 text-lg text-white/85 italic max-w-2xl">{f.tagline}</p>
            <div className="mt-8">
              <BrochureCta defaultFormula="any" variant="primary" />
            </div>
          </div>
        </div>
      </section>

      {/* PITCH */}
      <section className="dh-container py-20 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="dh-overline">{f.overline}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              {f.title} <span className="italic text-[color:hsl(var(--dh-terracotta))]">{f.titleItalic}</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[color:hsl(var(--dh-muted))] text-base leading-relaxed">
            <p>{f.pitch1}</p>
            <p>{f.pitch2}</p>
            <p>{f.pitch3}</p>
          </div>
        </div>
      </section>

      {/* MAISON ENTIÈRE */}
      <section className="relative">
        <img src={HOUSE_IMG} alt={f.houseTitle} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[color:hsl(var(--dh-palm))]/85" />
        <div className="relative z-10 dh-container py-20 md:py-24 text-[color:hsl(var(--dh-sand))]">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-6">
              <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">{f.houseOverline}</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">{f.houseTitle}</h2>
            </div>
            <div className="lg:col-span-6 lg:pt-6">
              <p className="opacity-90 leading-relaxed text-base">{f.houseDesc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* BONUS INCLUS */}
      <section className="dh-container py-20 md:py-24">
        <div className="max-w-2xl mb-12">
          <div className="dh-overline">{f.bonusOverline}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">{f.bonusTitle}</h2>
          <p className="mt-4 text-[color:hsl(var(--dh-muted))]">{f.bonusIntro}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {f.bonus.map((text, i) => {
            const Icon = BONUS_ICONS[i % BONUS_ICONS.length];
            return (
              <div key={i} data-testid={`family-bonus-${i}`} className="dh-card p-5 flex items-start gap-4">
                <Icon className="h-5 w-5 text-[color:hsl(var(--dh-terracotta))] flex-shrink-0 mt-1" />
                <span className="text-sm">{text}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* AMBIANCE band */}
      <section className="relative h-[55vh] overflow-hidden">
        <img src={AMBIANCE} alt="Villa Hasna" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </section>

      {/* FORMULAS */}
      <section id="formulas" className="bg-white border-y border-[color:hsl(var(--dh-ocre)/0.15)]">
        <div className="dh-container py-20 md:py-28">
          <div className="max-w-2xl mb-14">
            <div className="dh-overline">{f.formulasOverline}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">{f.formulasTitle}</h2>
            <p className="mt-4 text-[color:hsl(var(--dh-muted))]">{f.formulasIntro}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {FAMILY_PACKAGES.map((pkg, i) => {
              const data = f.formulas[pkg.key];
              return (
                <div
                  key={pkg.key}
                  data-testid={`family-formula-${pkg.key}`}
                  className={`p-8 flex flex-col ${
                    pkg.accent
                      ? "bg-[color:hsl(var(--dh-palm))] text-[color:hsl(var(--dh-sand))] border border-[color:hsl(var(--dh-palm))]"
                      : "dh-card"
                  }`}
                >
                  <div className={`dh-overline ${pkg.accent ? "text-[color:hsl(var(--dh-ocre))]" : "text-[color:hsl(var(--dh-terracotta))]"}`}>
                    Formule {String.fromCharCode(8544 + i)}
                  </div>
                  <div className="font-serif text-4xl mt-2">{data.name}</div>

                  <div className="mt-8">
                    <div className="font-serif text-5xl">
                      {pkg.price.toLocaleString("fr-FR")} <span className="text-2xl">€</span>
                    </div>
                    <div className={`text-xs mt-2 ${pkg.accent ? "opacity-80" : "text-[color:hsl(var(--dh-muted))]"}`}>
                      {f.perWeek} · {f.forFamily}
                    </div>
                  </div>

                  <div className={`mt-8 pt-6 border-t ${pkg.accent ? "border-white/15" : "border-[color:hsl(var(--dh-ocre)/0.2)]"} space-y-3 flex-1`}>
                    <div className={`dh-overline ${pkg.accent ? "text-[color:hsl(var(--dh-ocre))]" : ""}`}>{f.plusIncluded}</div>
                    {data.plusPrefix && (
                      <div className={`text-xs italic ${pkg.accent ? "opacity-80" : "text-[color:hsl(var(--dh-muted))]"}`}>
                        {data.plusPrefix}
                      </div>
                    )}
                    {data.extras.map((text, j) => {
                      const IconKey = pkg.extraKeys[j];
                      const Icon = EXTRA_ICON[IconKey] || Check;
                      return (
                        <div key={j} className="flex items-baseline gap-2 text-sm">
                          <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${pkg.accent ? "text-[color:hsl(var(--dh-ocre))]" : "text-[color:hsl(var(--dh-terracotta))]"}`} />
                          <span>{text}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-8">
                    <BrochureCta defaultFormula={pkg.key} variant={pkg.accent ? "primary" : "primary"} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-xs text-[color:hsl(var(--dh-muted))] max-w-2xl">
            <p>{f.terms}</p>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-[color:hsl(var(--dh-palm))] text-[color:hsl(var(--dh-sand))]">
        <div className="dh-container py-20 text-center">
          <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">{f.ctaOverline}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">{f.ctaTitle}</h2>
          <p className="mt-4 opacity-85 max-w-xl mx-auto">{f.ctaSubtitle}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <BrochureCta defaultFormula="any" variant="primary" />
            <a href={OWNER.waLink} target="_blank" rel="noreferrer" data-testid="family-wa" className="inline-flex items-center gap-2 border border-[color:hsl(var(--dh-ocre))] px-6 py-3 text-sm uppercase tracking-widest hover:bg-[color:hsl(var(--dh-ocre)/0.15)] transition-colors">
              <Phone className="h-4 w-4" />
              +212 (0)7 18 11 54 19
            </a>
            <a href={`mailto:${OWNER.email}?subject=Séjour Famille Villa Hasna`} data-testid="family-mail" className="inline-flex items-center gap-2 border border-[color:hsl(var(--dh-ocre))] px-6 py-3 text-sm uppercase tracking-widest hover:bg-[color:hsl(var(--dh-ocre)/0.15)] transition-colors">
              <Mail className="h-4 w-4" />
              {OWNER.email}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
